/**
 * CategoryFilterBody Component
 *
 * Renders product category chips inside the FiltersModal body.
 * Each chip toggles its category in/out of the selected set.
 */

// React
import { useMemo } from 'react';

// Hooks and utilities
import { cn } from '@/lib/utils';
import { TEXT_CHIP_BASE, CHIP_UNSELECTED, CHIP_SELECTED } from './filterChipStyles';

/* ---- Layout constants ---- */
const WRAP_CLASSES = 'flex flex-wrap gap-3';

/* ---- Types ---- */
interface CategoryFilterBodyProps {
  selectedCategories: string[];
  onToggle: (category: string) => void;
  /** Union of every format's categories, from the server. The list page has
   *  no single format in scope and carries its own format filter, so the
   *  chips show the full union rather than one format's set. */
  categories: string[];
}

/* ---- Component ---- */
export function CategoryFilterBody({ selectedCategories, onToggle, categories }: CategoryFilterBodyProps) {
  const selectedSet = useMemo(() => new Set(selectedCategories), [selectedCategories]);

  return (
    <div className={WRAP_CLASSES}>
      {categories.map((category) => {
        const isSelected = selectedSet.has(category);
        return (
          <button
            key={category}
            type="button"
            role="checkbox"
            aria-checked={isSelected}
            className={cn(TEXT_CHIP_BASE, isSelected ? CHIP_SELECTED : CHIP_UNSELECTED)}
            onClick={() => onToggle(category)}
          >
            {category}
          </button>
        );
      })}
    </div>
  );
}
